/** @format */
import React, { FunctionComponent, ReactElement, useEffect, useState } from 'react';
import GridContainer from '../../components/Grid/GridContainer';
import PlatformResellerInterface from '../../interfaces/platform/PlatformResellerInterface';
import { Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@material-ui/core';
import CustomTextButtom from '../../components/Buttons/CustomTextButton';
import I18n from '../../components/Translate/I18n';
import { useDispatch } from 'react-redux';
import { useSimCardServiceGroupList } from '../../modules/lib/hooks/useSimCardServiceGroupList';
import { FeatureTypes } from '../../modules/features/FeaturesTypes';
import { ComponentByGroupConfiguration } from './component/ComponentByGroupConfiguration';
import { ItemLoaders } from '../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../helpers/FormGeneral';
import { SimCardServicesPartnerAllowedCollection } from '../../interfaces/platform/SimCardServicesPartnerAllowed';
import GridItem from '../../components/Grid/GridItem';
import Button from '@material-ui/core/Button';
import { ReactComponent as Cancel } from '../../assets/themes/OPL/img/icons/Cancel.svg';
import { PlatformResellerMvnoOperationSetupCollectionInterface } from '../../interfaces/platform/PlatformResellerMvnoOperationSetup';
import { MvnoOperationSetupCollectionInterface } from '../../interfaces/platform/MvnoOperatorSetup';
import { SimpleSelect } from '../../components/Adescom/SimpleSelect';
import { HeaderCheckBoxSelectListElement } from '../lib/search/listTableHeader/HeaderCheckBoxSelectList';

type ServicePartnerListType = {
    platformReseller: PlatformResellerInterface | null;
    open: boolean;
    closeDialog(): void;
};

export const ServicePartnerList: FunctionComponent<ServicePartnerListType> = ({ platformReseller, open, closeDialog }): ReactElement => {
    const dispatch = useDispatch();
    const listType = new FeatureTypes(process.env);
    const simCardServiceTypeGroup = useSimCardServiceGroupList(listType.operatorType);
    const [partnerAllowedList, setPartnerAllowedList] = useState<Array<{ typeId: number; enabled: number }> | null>(null);
    const [mvnoOperatorSetup, setMvnoOperatorSetup] = useState<MvnoOperationSetupCollectionInterface | null>(null);
    const [
        platformResellerMvnoOperationSetup,
        setPlatformResellerMvnoOperationSetup,
    ] = useState<PlatformResellerMvnoOperationSetupCollectionInterface | null>(null);
    const [allowedUseType, setAllowedUseType] = useState<Array<HeaderCheckBoxSelectListElement>>([]);
    const [useType, setUseType] = useState(-1);

    useEffect(() => {
        if (open) {
            setPlatformResellerMvnoOperationSetup(null);
        }
    }, [open]);

    useEffect(() => {
        if (mvnoOperatorSetup === null) {
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/mvno_operator_setups')),
                'MVNO_OPERATOR_SETUP_GET_FOR_RESELLER',
                [
                    { value: 'TARIFF', name: 'typeValue' },
                    { name: 'operatorType', value: 2 },
                ],
                [{ name: 'displayName', value: 'asc' }],
                (data: MvnoOperationSetupCollectionInterface): void => {
                    setMvnoOperatorSetup(data);
                },
                { dispatchAfterCallback: false, propagateDataToCallback: true }
            );
        }
    }, [dispatch, mvnoOperatorSetup]);

    useEffect(() => {
        if (platformResellerMvnoOperationSetup) {
            return;
        }
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.getListWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/platform_reseller_mvno_operator_setups')),
            'MVNO_OPERATOR_SETUP_GET_FOR_RESELLER_COLLECTION',
            [
                { value: platformReseller ? platformReseller.id : -1, name: 'platformReseller' },
                { name: 'statusFlag[lt]', value: 10 },
            ],
            [],
            (data: PlatformResellerMvnoOperationSetupCollectionInterface): void => {
                setPlatformResellerMvnoOperationSetup(data);
                if (mvnoOperatorSetup === null) {
                    return;
                }
                const localList: Array<HeaderCheckBoxSelectListElement> = [];
                for (let i = 0; i < data['hydra:member'].length; i++) {
                    for (let j = 0; j < mvnoOperatorSetup['hydra:member'].length; j++) {
                        if (
                            mvnoOperatorSetup['hydra:member'][j]['@id'] ===
                            data['hydra:member'][i]['mvnoOperatorSetup'].replace(/base-api/, 'adescom-api')
                        ) {
                            localList.push({
                                name: mvnoOperatorSetup['hydra:member'][j].displayName,
                                value: mvnoOperatorSetup['hydra:member'][j].id,
                            });
                        }
                    }
                }
                setAllowedUseType(localList);
            },
            { dispatchAfterCallback: false, propagateDataToCallback: true }
        );
    }, [platformReseller, mvnoOperatorSetup, platformResellerMvnoOperationSetup, dispatch]);

    useEffect(() => {
        if (platformReseller && partnerAllowedList === null) {
            const itemLoaders = new ItemLoaders(dispatch);
            itemLoaders.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/sim_card_services_partner_alloweds')),
                'load_sim_card_services_partner_allowed_partner_' + platformReseller.id,
                [
                    { name: 'platformResellerId', value: platformReseller.id },
                    { name: 'enabled[]', value: -1 },
                    { name: 'enabled[]', value: 0 },
                    { name: 'enabled[]', value: 1 },
                    { name: 'enabled[]', value: 2 },
                    { name: 'enabled[]', value: 3 },
                    { name: 'useType', value: useType },
                ],
                [],
                (data: SimCardServicesPartnerAllowedCollection): void => {
                    const elements: Array<{ typeId: number; enabled: number }> = [];
                    for (let i = 0; i < data['hydra:member'].length; i++) {
                        elements.push({ typeId: data['hydra:member'][i].simCardServicesTypesId, enabled: data['hydra:member'][i].enabled });
                    }
                    setPartnerAllowedList(elements);
                },
                { propagateDataToCallback: true, dispatchAfterCallback: false }
            );
        }
    }, [dispatch, platformReseller, partnerAllowedList, useType]);

    const onChangeSelectEvent = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const field = event.target.name;
        const selected = parseInt(event.target.value);
        setUseType(selected);
        setPartnerAllowedList(null);
    };

    return (
        <Dialog open={open} maxWidth="md">
            {platformReseller && (
                <React.Fragment>
                    <DialogTitle>
                        <GridContainer justifyContent={'space-between'}>
                            <GridItem xs={11}>
                                <h4>
                                    <I18n trans="prov:feature.allowed_component" valueObject={{ partner: platformReseller.mainname }} />
                                </h4>
                            </GridItem>
                            <GridItem xs={1}>
                                <Button
                                    onClick={(): void => {
                                        setPartnerAllowedList(null);
                                        closeDialog();
                                    }}
                                    color="primary"
                                >
                                    <Cancel width={24} height={24} />
                                </Button>
                            </GridItem>
                            <GridItem xs={6}>
                                <GridItem xs={6} style={{ paddingTop: '8px' }}>
                                    <SimpleSelect
                                        label="prov:feature.profile_use_type"
                                        fieldName="useType"
                                        required={true}
                                        elements={allowedUseType}
                                        dispatchName={'platform_reseller_use_type'}
                                        selectedValue={useType}
                                        callback={onChangeSelectEvent}
                                    />
                                </GridItem>
                            </GridItem>
                        </GridContainer>
                    </DialogTitle>
                    {platformReseller && useType !== -1 && partnerAllowedList && (
                        <DialogContent>
                            <GridContainer>
                                {simCardServiceTypeGroup &&
                                    simCardServiceTypeGroup['hydra:member'].map((group) => {
                                        return (
                                            <ComponentByGroupConfiguration
                                                key={'group_allowed_service_partner_' + group.id}
                                                platformReseller={platformReseller}
                                                groupId={group.id}
                                                groupName={group.mainname}
                                                partnerAllowedList={partnerAllowedList}
                                                useType={useType}
                                            />
                                        );
                                    })}
                            </GridContainer>
                        </DialogContent>
                    )}
                </React.Fragment>
            )}
        </Dialog>
    );
};
